import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxResponseMessage } from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { Textarea } from 'bones/design-system/components/form/Textarea';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { Modal } from 'bones/design-system/components/Modal';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';

export interface VoiceboxChatFeedbackDialogProps {
  isOpen: boolean;
  message: VoiceboxResponseMessage;
  onClose: () => void;
}

export const VoiceboxChatFeedbackDialog: React.VFC<
  VoiceboxChatFeedbackDialogProps
> = ({ isOpen, message, onClose }) => {
  const isMounted = useIsMounted();
  const { copy, testIds } = useVoiceboxSchema();
  const { isLoadingFeedback, sendFeedback } = useVoiceboxConversation();

  const [feedbackComment, setFeedbackComment] = React.useState('');
  const trimmed = feedbackComment.trim();

  const handleCloseDialog = () => {
    setFeedbackComment('');
    onClose();
  };

  const handleSubmit = async () => {
    const { succeeded } = await sendFeedback(
      message,
      message.feedbackScore,
      trimmed
    );
    if (isMounted.current && succeeded) {
      handleCloseDialog();
    }
  };

  return (
    <Modal
      className="vb-voicebox-chat-feedback-dialog"
      footerActions={
        <>
          <Button
            disabled={isLoadingFeedback}
            onClick={handleCloseDialog}
            size="sm"
            testId={testIds.feedback.dialog.cancelButton}
            variant="secondary2-gray"
          >
            {copy.feedback.dialog.cancelButton}
          </Button>
          <Button
            disabled={isLoadingFeedback}
            onClick={handleSubmit}
            rightIcon={isLoadingFeedback ? LoadingIcon : undefined}
            rightIconProps={
              isLoadingFeedback
                ? {
                    testId: testIds.feedback.dialog.loading,
                  }
                : undefined
            }
            size="sm"
            testId={testIds.feedback.dialog.submitButton}
            variant="primary-accent"
          >
            {copy.feedback.dialog.submitButton}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleCloseDialog}
      title={copy.feedback.dialog.title}
    >
      <Textarea
        autoFocus
        disabled={isLoadingFeedback}
        growVertically={process.env.NODE_ENV !== 'test'}
        label=""
        onChange={(evt) => {
          setFeedbackComment(evt.target.value);
        }}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();

            // istanbul ignore if
            if (isLoadingFeedback) {
              return;
            }

            handleSubmit();
          }
        }}
        placeholder={copy.feedback.dialog.negativePlaceholder}
        testId={testIds.feedback.dialog.input}
        value={feedbackComment}
      />
    </Modal>
  );
};
